import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Lock, Unlock, Plus, Settings as SettingsIcon, Workflow, Sun, Moon, Sparkles, School, Home, Pencil } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { speak, cancelSpeech } from "@/lib/tts";
import { playAudioDataUrl } from "@/lib/media";
import { t, ttsLangFor } from "@/lib/i18n";
import { fireFirstWordConfetti } from "@/lib/celebrate";
import { maybeAutoBackup } from "@/lib/autoBackup";
import { useDayNightMode } from "@/lib/useDayNightMode";
import { useSchoolModeAuto } from "@/lib/useSchoolModeAuto";
import Tile from "@/components/Tile";
import SortableTile from "@/components/SortableTile";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, rectSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import SentenceStrip from "@/components/SentenceStrip";
import CategoryNav from "@/components/CategoryNav";
import TileEditor from "@/components/TileEditor";
import CategoryEditor from "@/components/CategoryEditor";
import PinPrompt from "@/components/PinPrompt";
import PredictiveBar from "@/components/PredictiveBar";
import SentenceScaffolds from "@/components/SentenceScaffolds";
import FirstWordToast from "@/components/FirstWordToast";
import OnboardingTour from "@/components/OnboardingTour";
import FamilyNoteBanner from "@/components/FamilyNoteBanner";
import LockdownReleaseButton from "@/components/LockdownReleaseButton";
import { serializeProfile as serializeProfileFor, gridColsFor } from "@/lib/profileSerialize";
import { isChildDevice } from "@/lib/childDevice";
import { tileCapFor, sentenceBuilderEnabledFor } from "@/lib/boardLevels";
import {
  hydrateTilesWithLocalMedia, setLocalImage, setLocalAudio,
} from "@/lib/localMedia";
import { hydrateTilesWithLocalTaps, bumpLocalTap } from "@/lib/localTaps";

export default function Board() {
  const { profileId } = useParams();
  const nav = useNavigate();

  const [profile, setProfile] = useState(null);
  const [categories, setCategories] = useState([]);
  const [tiles, setTiles] = useState([]);
  const [activeCat, setActiveCat] = useState("__favorites__");
  const [strip, setStrip] = useState([]); // [{label, emoji, color, speak_text}]
  const [pressedId, setPressedId] = useState(null);
  const [speaking, setSpeaking] = useState(false);

  const [editing, setEditing] = useState(false);
  const [tileEditor, setTileEditor] = useState({ open: false, initial: null });
  const [catEditor, setCatEditor] = useState({ open: false, initial: null });
  const [pinPrompt, setPinPrompt] = useState({ open: false, mode: "verify", error: "" });
  const [error, setError] = useState("");
  const [firstWordToast, setFirstWordToast] = useState(false);
  const [scanIdx, setScanIdx] = useState(0);

  const lang = profile?.language || "en";
  const ttsLang = ttsLangFor(lang);
  const dwellMs = profile?.dwell_ms || 0;
  const scanMode = !editing && !!profile?.scan_mode;
  const scanIntervalMs = profile?.scan_interval_ms || 1500;
  const highContrast = !!profile?.high_contrast;
  const reducedMotion = !!profile?.reduced_motion;
  const lockdown = !!profile?.lockdown;
  // Kid mode = clean board (no Edit/Settings/Coach/Modeling chrome).
  // Defaults TRUE for every profile so the device-shown-to-the-child is
  // simple. Caregivers flip it off (PIN-gated) to edit. Auto-flips back on
  // after 5 minutes of inactivity so a parent doesn't accidentally leave
  // the chrome exposed.
  //
  // CHILD-DEVICE LOCK: if this tablet is paired as a child device, kid mode
  // is HARD-ON regardless of the profile flag and the "Caregiver" toggle is
  // not even rendered. Exiting requires the parent to unpair from another
  // device — see /lib/childDevice.js.
  const childDeviceLocked = isChildDevice();
  const kidMode = childDeviceLocked || (profile ? (profile.kid_mode !== false) : true);
  const [modelingMode, setModelingMode] = useState(false);

  // Build the canonical PUT body for /profiles/:id from current state +
  // optional overrides. Thin wrapper around the pure helper in
  // /lib/profileSerialize.js so toggle handlers don't have to thread the
  // profile object through every call.
  const serializeProfile = (overrides = {}) => serializeProfileFor(profile, overrides);

  // When the profile loads / reloads, sync modelingMode from server
  useEffect(() => {
    if (profile) setModelingMode(!!profile.modeling_mode);
  }, [profile?.id, profile?.modeling_mode]);

  // Apply theme classes (high contrast / reduced motion) to <html> while on this page
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("hc-mode", highContrast);
    root.classList.toggle("no-motion", reducedMotion);
    return () => {
      root.classList.remove("hc-mode");
      root.classList.remove("no-motion");
    };
  }, [highContrast, reducedMotion]);

  // -------- Day / Night mode --------
  const mode = useDayNightMode(profile);

  // Cycle override: auto -> day -> night -> auto
  const cycleModeOverride = () => {
    if (!profile) return;
    const order = ["auto", "day", "night"];
    const cur = profile.mode_override || "auto";
    const next = order[(order.indexOf(cur) + 1) % order.length];
    setProfile((p) => p ? { ...p, mode_override: next } : p);
    api.put(`/profiles/${profileId}`, serializeProfile({ mode_override: next })).catch(() => {});
  };

  const load = useCallback(async () => {
    try {
      const [{ data: p }, { data: cats }, { data: ts }] = await Promise.all([
        api.get(`/profiles/${profileId}`),
        api.get(`/profiles/${profileId}/categories`),
        api.get(`/profiles/${profileId}/tiles`),
      ]);
      setProfile(p);
      setCategories(cats);
      // Phase 3 / local-first: server returns tiles WITHOUT photos or
      // voice. Hydrate each tile from this device's IndexedDB so the
      // existing rendering / TTS paths keep working unchanged.
      const withMedia = await hydrateTilesWithLocalMedia(profileId, ts);
      // Phase-3 tap-events ruling (2026-06): tap counts + last_tapped
      // ALSO live only on-device. Server no longer stores them, so we
      // rehydrate from the local IndexedDB tap store here.
      const hydrated = await hydrateTilesWithLocalTaps(profileId, withMedia);
      setTiles(hydrated);
      if (cats.length > 0 && activeCat === "__favorites__" && hydrated.filter(t => t.is_favorite).length === 0) {
        setActiveCat(cats[0].id);
      }
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      if (e.response?.status === 404) nav("/profiles");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId, nav]);

  useEffect(() => { load(); return () => cancelSpeech(); }, [load]);

  // ---- Live-sync polling (P1 — Draft → Synced) -----------------------------
  // Polls the lightweight /revision endpoint every 6s. When the server's
  // board_revision counter changes (the parent edited a tile from another
  // device), we silently refetch tiles + categories. The polling pauses
  // when the tab is hidden to save battery on a paired iPad in a stroller.
  useEffect(() => {
    if (!profileId) return undefined;
    let lastRevision = null;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      try {
        const { data } = await api.get(`/profiles/${profileId}/revision`);
        const r = data?.board_revision ?? 0;
        if (lastRevision === null) {
          lastRevision = r;
          return;
        }
        if (r !== lastRevision) {
          lastRevision = r;
          load();
        }
      } catch { /* offline / transient — skip this tick */ }
    };
    const id = setInterval(tick, 6000);
    const onVis = () => { if (document.visibilityState === "visible") tick(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [profileId, load]);

  // Once the profile + tiles are loaded, snapshot them to IndexedDB every 24h.
  useEffect(() => {
    if (!profile || !categories.length) return;
    maybeAutoBackup(profileId, async () => ({
      profile,
      categories: categories.map((c) => ({ ...c })),
      tiles: tiles.map((t) => {
        // Strip heavy media from auto-snapshot so quotas don't blow up;
        // user can still do a full Export → JSON in Settings.
        const { custom_audio_b64, custom_image_b64, ...rest } = t;
        void custom_audio_b64; void custom_image_b64;
        return rest;
      }),
    }));
  }, [profileId, profile?.id, categories.length, tiles.length]);

  // -------- Switch scanning: auto-advance highlight, Space/Enter to select. --------
  useEffect(() => {
    if (!scanMode) return;
    setScanIdx(0);
  }, [scanMode, activeCat]);

  useEffect(() => {
    if (!scanMode) return undefined;
    if (visibleTilesCount() === 0) return undefined;
    const id = setInterval(() => {
      setScanIdx((i) => (i + 1) % Math.max(1, visibleTilesCount()));
    }, scanIntervalMs);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanMode, scanIntervalMs, tiles.length, activeCat]);

  useEffect(() => {
    if (!scanMode) return undefined;
    const onKey = (e) => {
      // Any Space, Enter or any key from a switch interface = select current tile
      if (e.key === " " || e.key === "Enter" || e.key === "Spacebar") {
        e.preventDefault();
        const list = visibleTilesList();
        const tile = list[scanIdx % Math.max(1, list.length)];
        if (tile) onTileTap(tile);
      }
      if (e.key === "ArrowRight") setScanIdx((i) => (i + 1) % Math.max(1, visibleTilesCount()));
      if (e.key === "ArrowLeft") setScanIdx((i) => (i - 1 + Math.max(1, visibleTilesCount())) % Math.max(1, visibleTilesCount()));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanMode, scanIdx, tiles, activeCat]);

  // Tap-anywhere-on-screen activation for switch users (single-button input)
  useEffect(() => {
    if (!scanMode) return undefined;
    const onTap = (e) => {
      // ignore taps on existing UI controls
      if (e.target.closest("button, a, input, textarea, select, .pill-btn")) return;
      const list = visibleTilesList();
      const tile = list[scanIdx % Math.max(1, list.length)];
      if (tile) onTileTap(tile);
    };
    document.addEventListener("click", onTap, true);
    return () => document.removeEventListener("click", onTap, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanMode, scanIdx, tiles, activeCat]);

  // -------- School-time mode (auto Mon–Fri 8a–3p, parent override) --------
  const schoolOn = useSchoolModeAuto(profile);

  // Cycle school override: auto -> home -> school -> auto
  const cycleSchoolOverride = () => {
    if (!profile) return;
    const order = ["auto", "home", "school"];
    const cur = profile.school_mode_override || "auto";
    const next = order[(order.indexOf(cur) + 1) % order.length];
    setProfile((p) => p ? { ...p, school_mode_override: next } : p);
    api.put(`/profiles/${profileId}`, serializeProfile({ school_mode_override: next })).catch(() => {});
  };

  const visibleTiles = useMemo(() => {
    let base;
    if (activeCat === "__favorites__") {
      base = tiles.filter((t) => t.is_favorite);
      // Fresh boards start with zero favorites. Rather than making the
      // parent hunt for the category tab, fall through to showing every
      // tile so their newly-created words appear right away. See
      // iter-defect #1 (2026-07-04 "words don't reach the board").
      if (base.length === 0 && !editing) base = tiles;
    }
    else if (activeCat === "__most_used__") {
      base = [...tiles].filter((t) => (t.tap_count || 0) > 0).sort((a, b) => (b.tap_count || 0) - (a.tap_count || 0)).slice(0, 24);
      if (base.length === 0 && !editing) base = tiles;
    } else {
      base = tiles.filter((t) => t.category_id === activeCat);
    }
    // School-time scope filter (only applied in kid-facing view; caregivers
    // editing the board should see every tile so they can manage scopes).
    if (!editing) {
      base = base.filter((t) => {
        const scope = t.time_scope || "anytime";
        if (scope === "anytime") return true;
        if (scope === "home_only") return !schoolOn;
        if (scope === "school_only") return schoolOn;
        return true;
      });
    }
    // In night mode, narrow to the curated bedtime vocabulary so a 2am
    // wake-up doesn't have to scroll through 50 daytime tiles.
    if (mode === "night") {
      const nightSet = new Set((profile?.night_tiles || []).map((s) => s.toLowerCase().trim()));
      if (nightSet.size > 0) {
        const nightFiltered = base.filter((t) => nightSet.has((t.label || "").toLowerCase().trim()));
        // If the current category has nothing relevant, just show every
        // matching tile across the board so the kid is never stuck.
        if (nightFiltered.length === 0) {
          base = tiles.filter((t) => nightSet.has((t.label || "").toLowerCase().trim()));
        } else {
          base = nightFiltered;
        }
      }
    }
    // Board growth level cap (P1). Hard-cap the child-facing board so a
    // starter6 user never sees more than 6 tiles even if extras exist in
    // the database. We DO NOT delete tiles — extras just stay hidden
    // until the parent raises the level. Caregiver edit view (Manage
    // Board) is unaffected because it queries tiles directly and doesn't
    // use this list.
    const cap = tileCapFor(profile?.board_level);
    if (cap != null && !editing) {
      base = base.slice(0, cap);
    }
    return base;
  }, [tiles, activeCat, mode, profile?.night_tiles, profile?.board_level, schoolOn, editing]);

  const visibleTilesList = () => visibleTiles;
  const visibleTilesCount = () => visibleTiles.length;

  // Map of category-id → category color, for tile background inheritance.
  // Hook is at top level (always called) to comply with rules-of-hooks.
  const categoryColorById = useMemo(() => {
    const m = new Map();
    for (const c of categories) m.set(c.id, c.color);
    return m;
  }, [categories]);

  const favoritesCount = useMemo(() => tiles.filter((t) => t.is_favorite).length, [tiles]);
  const mostUsedCount = useMemo(() => tiles.filter((t) => (t.tap_count || 0) > 0).length, [tiles]);
  const tilesByLabel = useMemo(() => {
    const m = new Map();
    tiles.forEach((t) => m.set((t.label || "").toLowerCase(), t));
    return m;
  }, [tiles]);

  // ---------------- TTS ----------------
  // Provenance for pilot instrumentation. Three-valued actor:
  // - "caregiver_editing"  = tile editor open (preview taps while setting up)
  // - "caregiver_modeling" = modeling mode badge visible (intentional demo)
  // - "kid"                = the non-verbal user actually trying to communicate
  // Distinct buckets matter: an SLP's modeling is therapeutically meaningful,
  // but an editor preview is engineering noise.
  const tapActor = () =>
    editing ? "caregiver_editing" : modelingMode ? "caregiver_modeling" : "kid";
  // Active mode at tap time, snapshotted at the event so historical
  // analysis still works even after the schedule changes.
  const tapModeSnapshot = () => (mode === "night" ? "night" : schoolOn ? "school" : "home");
  // Input modality currently engaged (touch is the default).
  const tapInputModality = () =>
    scanMode ? "scan" : (Number(profile?.dwell_ms) || 0) > 0 ? "dwell" : "touch";

  const trackTap = (tileId, tile, { spokenOk = null } = {}) => {
    // STRICT LOCAL-ONLY (founder ruling 2026-06): button-press history
    // never leaves the device. We persist the counter + last-tapped ISO
    // to IndexedDB via /lib/localTaps and NO LONGER POST to the server.
    // The provenance fields (actor / mode / input / spoken_ok) are no
    // longer collected — we're intentionally blind to behavioral data.
    void tapActor; void tapModeSnapshot; void tapInputModality; void spokenOk;
    bumpLocalTap(profileId, tileId).catch(() => {});
    setTiles((arr) =>
      arr.map((t) =>
        t.id === tileId
          ? { ...t, tap_count: (t.tap_count || 0) + 1, last_tapped: new Date().toISOString() }
          : t,
      ),
    );
    // Block 5: log a communication-history event when the profile has opted in.
    // BE silently 204s if mode == "off" so this is safe to fire blindly.
    if (profile?.history_mode && profile.history_mode !== "off" && tile) {
      api.post(`/profiles/${profileId}/history`, {
        spoken_text: tile.speak_text || tile.label,
        label: tile.label,
        emoji: tile.emoji,
        category_id: tile.category_id,
        tile_id: tile.id,
        mode_when_spoken: profile?.mode_override || "auto",
      }).catch(() => {});
    }
  };

  const speakTile = async (tile) => {
    setPressedId(tile.id);
    setTimeout(() => setPressedId(null), 220);
    maybeCelebrateFirstWord();

    let spokenOk = null;
    if (tile.custom_audio_b64) {
      cancelSpeech();
      playAudioDataUrl(tile.custom_audio_b64);
      // Custom audio path — we trust the browser fired.
      spokenOk = true;
    } else {
      // Race TTS against a 4-second safety timeout so a backgrounded tab
      // or an iOS Safari rate-limit doesn't leave the tap event un-logged.
      const result = await Promise.race([
        speak(tile.speak_text || tile.label, {
          rate: profile?.voice_rate ?? 0.95,
          pitch: profile?.voice_pitch ?? 1.05,
          voiceName: profile?.voice_name,
          lang: ttsLang,
        }),
        new Promise((r) => setTimeout(() => r({ spoken_ok: null }), 4000)),
      ]);
      spokenOk = result?.spoken_ok ?? null;
    }
    // Exactly ONE tap event per tap. Provenance is settled by now.
    trackTap(tile.id, tile, { spokenOk });
  };

  const maybeCelebrateFirstWord = () => {
    if (!profile || profile.first_word_celebrated) return;
    // Optimistically mark celebrated locally so we never fire twice in a session.
    setProfile((p) => (p ? { ...p, first_word_celebrated: true } : p));
    api
      .post(`/profiles/${profileId}/celebrate-first-word`)
      .then(({ data }) => {
        // Server-confirmed first word for this profile — fire the moment.
        if (data?.already) return;
        fireFirstWordConfetti();
        setFirstWordToast(true);
        // Speak the encouragement after a tiny delay so it follows the tile word.
        setTimeout(() => {
          speak(t(lang, "board.first_word.speak"), {
            rate: 1.0,
            pitch: 1.15,
            voiceName: profile?.voice_name,
            lang: ttsLang,
          });
        }, 1100);
      })
      .catch(() => {
        // Offline / API down — don't celebrate, but keep the local flag so we
        // don't keep retrying on every tap. It will retry next session.
      });
  };

  const onTileTap = (tile) => {
    if (editing) return; // taps are inert in edit mode
    speakTile(tile);
    // Modeling mode: caregiver demonstrates without polluting the strip.
    if (modelingMode) return;
    // Sentence Mode toggle (defect #8, 2026-07-04): if the caregiver hasn't
    // enabled Sentence Builder for this profile, single-tap-speaks is the
    // whole interaction — no strip, no chained sentence. Just speak.
    if (!sentenceBuilderEnabledFor(profile?.board_level, profile?.sentence_builder_enabled)) return;
    setStrip((s) => [
      ...s,
      {
        id: tile.id,
        label: tile.label,
        emoji: tile.emoji,
        color: tile.color,
        speak_text: tile.speak_text || tile.label,
        custom_audio_b64: tile.custom_audio_b64 || null,
        custom_image_b64: tile.custom_image_b64 || null,
      },
    ]);
  };

  const speakSentence = async () => {
    if (strip.length === 0) return;
    setSpeaking(true);
    cancelSpeech();
    // Walk through the strip; play recorded audio when present, otherwise TTS that segment.
    let i = 0;
    while (i < strip.length) {
      const item = strip[i];
      if (item.custom_audio_b64) {
        await playAudioDataUrl(item.custom_audio_b64);
        i += 1;
      } else {
        // Group consecutive non-recorded items into one TTS utterance for natural prosody.
        let j = i;
        const phrase = [];
        while (j < strip.length && !strip[j].custom_audio_b64) {
          phrase.push(strip[j].speak_text);
          j += 1;
        }
        await speak(phrase.join(" "), {
          rate: profile?.voice_rate ?? 0.95,
          pitch: profile?.voice_pitch ?? 1.05,
          voiceName: profile?.voice_name,
          lang: ttsLang,
        });
        i = j;
      }
    }
    setSpeaking(false);
  };

  // ---------------- Edit mode toggle (PIN-protected) ----------------
  const requestEditMode = () => {
    if (editing) {
      setEditing(false);
      return;
    }
    if (profile?.pin) {
      setPinPrompt({ open: true, mode: "verify", error: "" });
    } else {
      setEditing(true);
    }
  };

  const handlePinSubmit = (pin, errMsg) => {
    if (errMsg) {
      setPinPrompt((p) => ({ ...p, error: errMsg }));
      return;
    }
    if (pinPrompt.mode === "verify") {
      const expected = profile.caregiver_pin || profile.pin;
      if (pin === expected) {
        setPinPrompt({ open: false, mode: "verify", error: "" });
        // PIN unlocks BOTH kid-mode flip and editing
        if (kidMode) {
          setProfile((p) => p ? { ...p, kid_mode: false } : p);
          api.put(`/profiles/${profileId}`, serializeProfile({ kid_mode: false })).catch(() => {});
        } else {
          setEditing(true);
        }
      } else {
        setPinPrompt((p) => ({ ...p, error: "Incorrect PIN" }));
      }
    }
  };

  // ---------------- Tile CRUD ----------------
  // Phase 3 / local-first: pull `custom_image_b64` and `custom_audio_b64`
  // OUT of the payload before sending to the server. The cloud body holds
  // only structure; media goes straight to IndexedDB.
  const saveTile = async (data) => {
    try {
      const { custom_image_b64, custom_audio_b64, ...cloudBody } = data;
      if (tileEditor.initial?.id) {
        const tileId = tileEditor.initial.id;
        if (custom_image_b64 !== undefined) await setLocalImage(profileId, tileId, custom_image_b64 || null);
        if (custom_audio_b64 !== undefined) await setLocalAudio(profileId, tileId, custom_audio_b64 || null);
        const { data: updated } = await api.put(`/tiles/${tileId}`, cloudBody);
        // Re-attach the local media so the in-memory tile renders unchanged.
        const decorated = {
          ...updated,
          ...(custom_image_b64 !== undefined ? { custom_image_b64: custom_image_b64 || null } : {}),
          ...(custom_audio_b64 !== undefined ? { custom_audio_b64: custom_audio_b64 || null } : {}),
        };
        setTiles((arr) => arr.map((t) => (t.id === updated.id ? decorated : t)));
      } else {
        const { data: created } = await api.post(`/profiles/${profileId}/tiles`, cloudBody);
        if (custom_image_b64) await setLocalImage(profileId, created.id, custom_image_b64);
        if (custom_audio_b64) await setLocalAudio(profileId, created.id, custom_audio_b64);
        const decorated = {
          ...created,
          ...(custom_image_b64 ? { custom_image_b64 } : {}),
          ...(custom_audio_b64 ? { custom_audio_b64 } : {}),
        };
        setTiles((arr) => [...arr, decorated]);
      }
      setTileEditor({ open: false, initial: null });
    } catch (e) {
      alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const deleteTile = async (tile) => {
    if (!window.confirm(`Delete "${tile.label}"?`)) return;
    try {
      await api.delete(`/tiles/${tile.id}`);
      setTiles((arr) => arr.filter((t) => t.id !== tile.id));
    } catch (e) {
      alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const toggleFavorite = async (tile) => {
    try {
      const { data: updated } = await api.post(`/tiles/${tile.id}/toggle-favorite`);
      setTiles((arr) => arr.map((t) => (t.id === updated.id ? updated : t)));
    } catch (e) {
      alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  // ---------------- Category CRUD ----------------
  const saveCategory = async (data) => {
    try {
      if (catEditor.initial?.id) {
        const { data: updated } = await api.put(`/categories/${catEditor.initial.id}`, data);
        setCategories((arr) => arr.map((c) => (c.id === updated.id ? updated : c)));
      } else {
        const { data: created } = await api.post(`/profiles/${profileId}/categories`, {
          ...data,
          order: categories.length,
        });
        setCategories((arr) => [...arr, created]);
      }
      setCatEditor({ open: false, initial: null });
    } catch (e) {
      alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const deleteCategory = async (cat) => {
    if (!window.confirm(`Delete "${cat.name}" and all of its tiles?`)) return;
    try {
      await api.delete(`/categories/${cat.id}`);
      setCategories((arr) => arr.filter((c) => c.id !== cat.id));
      setTiles((arr) => arr.filter((t) => t.category_id !== cat.id));
      if (activeCat === cat.id) setActiveCat("__favorites__");
    } catch (e) {
      alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  // ---------------- Reorder ----------------
  const reorderCategories = async (next) => {
    setCategories(next);
    try {
      await api.put(`/profiles/${profileId}/categories/reorder`,
        next.map((c, i) => ({ id: c.id, order: i })),
      );
    } catch (e) {
      alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onTilesDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = visibleTiles.findIndex((t) => t.id === active.id);
    const newIdx = visibleTiles.findIndex((t) => t.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const reordered = arrayMove(visibleTiles, oldIdx, newIdx);
    // Update tiles state by replacing the affected ones with new order values
    setTiles((arr) => {
      const updated = [...arr];
      reordered.forEach((t, i) => {
        const idx = updated.findIndex((x) => x.id === t.id);
        if (idx >= 0) updated[idx] = { ...updated[idx], order: i };
      });
      return updated;
    });
    try {
      await api.put(`/profiles/${profileId}/tiles/reorder`,
        reordered.map((t, i) => ({ id: t.id, order: i })),
      );
    } catch (e) {
      alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="board-loading">
        <div className="display text-2xl">{error || "Loading board…"}</div>
      </div>
    );
  }

  // Tile size at night: bump up one notch so fewer-but-bigger tiles are
  // easier to find with sleepy eyes.
  const baseSize = profile.tile_size || "medium";
  const tileSize = mode === "night"
    ? (baseSize === "small" ? "medium" : "large")
    : baseSize;

  // Grid density (2..5) wins over tile_size if set. Caregivers tune
  // density per child; tile_size still controls how big each tile renders.
  // At night we drop density by 1 (min 2) to surface the bedtime vocab in
  // bigger, easier-to-tap chunks. (helper lives in /lib/profileSerialize.js)
  const gridCols = gridColsFor(profile.grid_density, mode);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Top bar — sticky above the tile grid; z-40 keeps the day/night
          and school toggles above any tile hover/active states. Wraps
          on iPad-portrait widths so pills never overlap (defect batch 3,
          2026-07-05). */}
      <header className="sticky top-0 z-40 px-4 sm:px-6 py-3 border-b-[3px] border-[color:var(--ink)] bg-[color:var(--cream)] flex items-center gap-3 flex-wrap">
        {!lockdown && !childDeviceLocked && (
          <Link to="/profiles" className="pill-btn" data-testid="board-back">
            <ArrowLeft size={16} />
          </Link>
        )}
        <div className="flex items-center gap-2 min-w-0">
          <div
            className="w-10 h-10 rounded-xl border-[3px] border-[color:var(--ink)] flex items-center justify-center text-2xl shrink-0"
            style={{ background: profile.color }}
          >
            {profile.avatar}
          </div>
          <div className="min-w-0">
            <div className="display text-base sm:text-xl font-extrabold leading-tight" data-testid="board-profile-name">
              {profile.name}'s board
            </div>
            <div className="text-xs text-[color:var(--ink-soft)] hidden sm:block">
              {modelingMode ? (
                <span className="text-[color:var(--coral-deep)] font-semibold">👀 Modeling — taps don't add to the strip</span>
              ) : (
                t(lang, "board.tap_hint")
              )}
            </div>
          </div>
        </div>
        <div className="flex-1" />
        {/* Day/Night toggle — always visible (even in lockdown) so a 2am
            parent can switch modes without unlocking. */}
        <button
          type="button"
          onClick={cycleModeOverride}
          className={`pill-btn ${mode === "night" ? "" : "gold"}`}
          aria-label={`Mode: ${profile.mode_override || "auto"} (showing ${mode})`}
          title={`Mode: ${profile.mode_override || "auto"} — currently ${mode}`}
          data-testid="board-mode-toggle"
        >
          {mode === "night" ? <Moon size={16} /> : <Sun size={16} />}
          <span className="hidden sm:inline">
            {(profile.mode_override || "auto") === "auto" ? `${mode} (auto)` : (profile.mode_override || "auto")}
          </span>
        </button>
        {/* School-mode toggle — auto Mon–Fri 8a–3p, parent can override.
            Always visible (even in lockdown) so a school-staff helper or
            parent can flip context without unlocking. Hidden if no tile
            on the board uses time_scope yet (avoids clutter for new users). */}
        {tiles.some((tt) => tt.time_scope && tt.time_scope !== "anytime") && (
          <button
            type="button"
            onClick={cycleSchoolOverride}
            className={`pill-btn ${schoolOn ? "primary" : ""}`}
            aria-label={`School mode: ${profile.school_mode_override || "auto"} (currently ${schoolOn ? "on" : "off"})`}
            title={`School mode override: ${profile.school_mode_override || "auto"} — currently ${schoolOn ? "ON (school)" : "OFF (home)"}`}
            data-testid="board-school-toggle"
          >
            {schoolOn ? <School size={16} /> : <Home size={16} />}
            <span className="hidden sm:inline">
              {(profile.school_mode_override || "auto") === "auto" ? `${schoolOn ? "school" : "home"} (auto)` : (profile.school_mode_override || "auto")}
            </span>
          </button>
        )}
        {lockdown ? (
          // Hold-to-exit pill: 3-second long-press releases the lock
          <LockdownReleaseButton
            onRelease={() => {
              setProfile((p) => p ? { ...p, lockdown: false } : p);
              api.put(`/profiles/${profileId}`, serializeProfile({ lockdown: false })).catch(() => {});
            }}
          />
        ) : kidMode ? (
          // KID MODE — minimal chrome. Just a small "Caregiver" pill so
          // mom/dad can flip back to edit. PIN-gated if the profile has
          // one set; otherwise immediate.
          //
          // On a PAIRED CHILD DEVICE (`childDeviceLocked`), even this pill
          // is gone — the child can't reach edit/admin chrome at all. The
          // parent unpairs from another device via /admin/child-devices.
          childDeviceLocked ? null : (
            <button
              type="button"
              onClick={() => {
                // Gate on EITHER field. `PUT /profiles/{id}/pin` stores the
                // PIN in `profile.pin`; older UI paths write `caregiver_pin`.
                // handlePinSubmit already reads both (`caregiver_pin || pin`);
                // this trigger must match, otherwise a child on a kid_mode
                // device can bypass the lock by tapping the Caregiver pill
                // whenever only `pin` is set. Reported in iteration_33.
                if (profile?.caregiver_pin || profile?.pin) {
                  setPinPrompt({ open: true, mode: "verify", error: "" });
                } else {
                  setProfile((p) => p ? { ...p, kid_mode: false } : p);
                  api.put(`/profiles/${profileId}`, serializeProfile({ kid_mode: false })).catch(() => {});
                }
              }}
              className="pill-btn"
              data-testid="board-caregiver-toggle"
              title="Switch to caregiver edit mode"
            >
              <span aria-hidden>🎓</span>
              <span className="hidden sm:inline">Caregiver</span>
            </button>
          )
        ) : (
          <>
            <button
              type="button"
              onClick={() => {
                setProfile((p) => p ? { ...p, kid_mode: true } : p);
                api.put(`/profiles/${profileId}`, serializeProfile({ kid_mode: true })).catch(() => {});
              }}
              className="pill-btn gold"
              data-testid="board-kid-mode-toggle"
              title="Hand the device back to the kid"
            >
              <span aria-hidden>👶</span>
              <span className="hidden sm:inline">Hand to kid</span>
            </button>
            <button
              type="button"
              onClick={() => {
                const next = !modelingMode;
                setModelingMode(next);
                // Persist asynchronously
                if (profile) {
                  setProfile((p) => p ? { ...p, modeling_mode: next } : p);
                  api.put(`/profiles/${profileId}`, serializeProfile({ modeling_mode: next })).catch(() => {});
                }
              }}
              className={`pill-btn ${modelingMode ? "gold" : ""}`}
              aria-pressed={modelingMode}
              title="Modeling — demonstrate without adding to the strip"
              data-testid="board-modeling-toggle"
            >
              <span aria-hidden>👀</span>
              <span className="hidden sm:inline">{modelingMode ? "Modeling" : "Model"}</span>
            </button>
            <Link to={`/first-then/${profileId}`} className="pill-btn" aria-label="First, then schedule" data-testid="board-first-then" title="First-Then schedule">
              <Workflow size={16} />
              <span className="hidden sm:inline">First, then</span>
            </Link>
            <Link to={`/choice/${profileId}`} className="pill-btn" aria-label="Choice board" data-testid="board-choice" title="Pick-one choice board">
              <span aria-hidden>🤔</span>
              <span className="hidden sm:inline">Choice</span>
            </Link>
            <Link to={`/coach/${profileId}`} className="pill-btn gold" aria-label="AI Coach" data-testid="board-coach" title="AI coach for caregivers">
              <Sparkles size={16} />
              <span className="hidden sm:inline">Coach</span>
            </Link>
            <Link
              to={`/manage/${profileId}?tab=create-button`}
              className="pill-btn primary"
              aria-label="Manage board"
              data-testid="board-manage"
              title="Edit buttons, colors, and modes (parent admin area)"
            >
              <Pencil size={16} />
              <span className="hidden sm:inline">Manage</span>
            </Link>
            <Link to={`/settings/${profileId}`} className="pill-btn" aria-label="Settings" data-testid="board-settings">
              <SettingsIcon size={16} />
            </Link>
            <button
              type="button"
              onClick={requestEditMode}
              className={`pill-btn ${editing ? "gold" : ""}`}
              aria-pressed={editing}
              data-testid="board-edit-toggle"
            >
              {editing ? <Unlock size={16} /> : <Lock size={16} />}
              <span className="hidden sm:inline">{editing ? t(lang, "board.done") : t(lang, "board.edit")}</span>
            </button>
          </>
        )}
      </header>

      {/* Sentence strip — only when caregiver has enabled Sentence Mode.
          Default OFF for all new profiles (defect #8, 2026-07-04). When
          disabled: single-tap-speaks is the whole interaction — no strip,
          no PredictiveBar, no Scaffolds. */}
      {sentenceBuilderEnabledFor(profile?.board_level, profile?.sentence_builder_enabled) && (
        <div className="px-4 sm:px-6 pt-4">
          <SentenceStrip
            items={strip}
            onSpeak={speakSentence}
            onBackspace={() => setStrip((s) => s.slice(0, -1))}
            onClear={() => setStrip([])}
            onRemoveAt={(idx) => setStrip((s) => s.filter((_, i) => i !== idx))}
            onSaveAsTile={() => {
              // Combine the strip into a brand-new "phrase tile" we can re-use later.
              const phrase = strip.map((s) => s.speak_text || s.label).join(" ");
              const cat = categories[0];
              if (!cat) return;
              setTileEditor({
                open: true,
                initial: {
                  label: phrase,
                  speak_text: phrase,
                  emoji: "💬",
                  color: cat.color,
                  category_id: cat.id,
                  is_favorite: true,
                },
              });
            }}
            speaking={speaking}
            lang={lang}
          />
          <PredictiveBar
            profileId={profileId}
            strip={strip}
            tilesByLabel={tilesByLabel}
            onPick={(tile) => onTileTap(tile)}
            enabled={profile?.ai_suggestions !== false}
            lang={lang}
          />
          {strip.length === 0 && (
            <SentenceScaffolds
              lang={lang}
              tilesByLabel={tilesByLabel}
              onPickFrame={(tiles) => {
                tiles.forEach((tile) => onTileTap(tile));
              }}
            />
          )}
        </div>
      )}

      {error && (
        <div className="px-4 sm:px-6 mt-3 text-[color:var(--coral-deep)]" data-testid="board-error">
          {error}
        </div>
      )}

      {/* Main grid — layout adapts to caregiver setting `category_layout`:
            "auto"    → mobile stacks, desktop shows left sidebar (default)
            "sidebar" → left sidebar at every breakpoint (adults / big vocab)
            "tabs"    → horizontal category tabs on top, no sidebar
      */}
      <main
        className={
          profile?.category_layout === "tabs"
            ? "flex-1 px-4 sm:px-6 py-4 flex flex-col gap-3"
            : profile?.category_layout === "sidebar"
              ? "flex-1 px-2 sm:px-4 py-4 flex flex-col gap-3"
              : "flex-1 px-4 sm:px-6 py-4 flex flex-col gap-3"
        }
        data-testid={`board-layout-${profile?.category_layout || "auto"}`}
      >
        {/* Family Note banner — pulses when a parent has sent a note.
            Sits above the board so the child sees it immediately on open. */}
        <FamilyNoteBanner profileId={profileId} />

        {/* School-mode contextual chip — quiet visual cue that a structured
            school-day filter is active. Shows in kid view only (caregivers
            already see the header pill), and only when at least one tile is
            scoped (so the child isn't confused on profiles without scoping). */}
        {schoolOn && !editing && tiles.some((tt) => tt.time_scope && tt.time_scope !== "anytime") && (
          <div
            className="mb-3 brutal-card bg-[color:var(--gold)] px-3 py-2 flex items-center gap-2 text-sm school-pulse"
            role="status"
            aria-live="polite"
            data-testid="board-school-mode-active"
          >
            <School size={16} className="shrink-0" />
            <span>
              <strong>School time</strong> · home-only tiles are tucked away
              until {profile?.school_end || "3:00pm"}.
            </span>
          </div>
        )}

        <div
          className={
            profile?.category_layout === "tabs"
              ? "grid grid-cols-1 gap-4"
              : profile?.category_layout === "sidebar"
                ? "grid grid-cols-[140px,1fr] sm:grid-cols-[200px,1fr] md:grid-cols-[260px,1fr] gap-3 sm:gap-4"
                : "grid grid-cols-1 md:grid-cols-[260px,1fr] gap-4"
          }
        >
        {profile?.category_layout !== "tabs" && (
          <aside className="md:sticky md:top-4 md:self-start">
            <CategoryNav
              categories={categories}
              activeId={activeCat}
              onSelect={setActiveCat}
              favoritesCount={favoritesCount}
              mostUsedCount={mostUsedCount}
              editing={editing}
              onAdd={() => setCatEditor({ open: true, initial: null })}
              onEditCategory={(c) => setCatEditor({ open: true, initial: c })}
              onDeleteCategory={deleteCategory}
              onReorder={reorderCategories}
              lang={lang}
            />
          </aside>
        )}
        {profile?.category_layout === "tabs" && (
          <div className="overflow-x-auto -mx-1 px-1 pb-1" data-testid="board-tabs-strip">
            <CategoryNav
              categories={categories}
              activeId={activeCat}
              onSelect={setActiveCat}
              favoritesCount={favoritesCount}
              mostUsedCount={mostUsedCount}
              editing={editing}
              onAdd={() => setCatEditor({ open: true, initial: null })}
              onEditCategory={(c) => setCatEditor({ open: true, initial: c })}
              onDeleteCategory={deleteCategory}
              onReorder={reorderCategories}
              lang={lang}
              horizontal
            />
          </div>
        )}

        <section
          className={`grid ${gridCols} gap-3 sm:gap-4 content-start`}
          data-testid="tile-grid"
        >
          <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={onTilesDragEnd}>
            <SortableContext items={visibleTiles.map((t) => t.id)} strategy={rectSortingStrategy}>
              {visibleTiles.map((t, idx) => (
                <SortableTile
                  key={t.id}
                  tile={t}
                  size={tileSize}
                  editing={editing && activeCat !== "__favorites__" && activeCat !== "__most_used__"}
                  pressed={pressedId === t.id}
                  dwellMs={dwellMs}
                  scanFocused={scanMode && idx === scanIdx % Math.max(1, visibleTiles.length)}
                  categoryColor={categoryColorById.get(t.category_id)}
                  onTap={onTileTap}
                  onEdit={(tile) => setTileEditor({ open: true, initial: tile })}
                  onDelete={deleteTile}
                  onToggleFavorite={toggleFavorite}
                />
              ))}
            </SortableContext>
          </DndContext>

          {editing && activeCat !== "__favorites__" && activeCat !== "__most_used__" && (
            <button
              type="button"
              onClick={() =>
                setTileEditor({
                  open: true,
                  initial: {
                    category_id: activeCat,
                    color: categories.find((c) => c.id === activeCat)?.color,
                  },
                })
              }
              className="tile-btn"
              style={{ borderStyle: "dashed", background: "transparent" }}
              data-testid="tile-add"
            >
              <Plus size={36} />
              <span className="tile-label">{t(lang, "board.add_tile")}</span>
            </button>
          )}

          {visibleTiles.length === 0 && !editing && (
            <div className="col-span-full brutal-card p-6 bg-white text-center" data-testid="empty-state">
              <div className="text-4xl mb-2" aria-hidden>✨</div>
              <div className="display text-xl font-bold mb-1">{t(lang, "board.empty.title")}</div>
              <div className="text-[color:var(--ink-soft)]">
                {activeCat === "__favorites__"
                  ? t(lang, "board.empty.favorites")
                  : activeCat === "__most_used__"
                  ? t(lang, "board.empty.most_used")
                  : t(lang, "board.empty.category")}
              </div>
            </div>
          )}
        </section>
        </div>
      </main>

      {/* Modals */}
      <TileEditor
        open={tileEditor.open}
        initial={tileEditor.initial}
        categories={categories}
        onClose={() => setTileEditor({ open: false, initial: null })}
        onSave={saveTile}
        lang={lang}
        profileId={profileId}
      />
      <CategoryEditor
        open={catEditor.open}
        initial={catEditor.initial}
        onClose={() => setCatEditor({ open: false, initial: null })}
        onSave={saveCategory}
      />
      <PinPrompt
        open={pinPrompt.open}
        mode={pinPrompt.mode}
        error={pinPrompt.error}
        onClose={() => setPinPrompt({ open: false, mode: "verify", error: "" })}
        onSubmit={handlePinSubmit}
      />
      <FirstWordToast
        open={firstWordToast}
        onDone={() => setFirstWordToast(false)}
        message={t(lang, "board.first_word.toast")}
      />
      {/* Onboarding tour — caregiver-facing copy ("Tap Edit in the top
          right", "Open Settings to set a PIN"). Do NOT show it on a
          paired child device or in kid_mode: the child would see caregiver
          instructions covering their tiles. Defect #5b (2026-07-04). */}
      {!childDeviceLocked && !kidMode && (
        <OnboardingTour
          profileId={profileId}
          profile={profile}
          onDone={() => setProfile((p) => (p ? { ...p, onboarded: true } : p))}
        />
      )}
    </div>
  );
}
